import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useStyles, ROW_H, LABEL_COL_W, HEADER_H } from './Styles';
import {
  ROW_KIND,
  STATUS,
  STATUS_COLOURS,
  ZOOM,
  barColourOf,
  barGeometry,
  dateWindowOf,
  elapsedFractionOf,
  flattenVisible,
  headerBandsOf,
  inclusiveDays,
  longDate,
  pillLabel,
  scaleOf,
  shortDate,
  todayXOf,
} from './ganttModel';

/**
 * The Gantt grid itself — timeline header, grid lines, rows, bars, today marker
 * and the row tooltip (Gantt Chart FSD F–K).
 *
 * Layout: the row-label column is pinned left (`position: sticky`) while the
 * timeline scrolls horizontally beneath a sticky two-tier header, per FSD H
 * ("The row label column ... remains pinned to the left of the screen while the
 * timeline scrolls horizontally underneath the sticky header").
 *
 * Presentation only — every rule about what a row IS (status, rollup, span,
 * colour, elapsed fraction, which ticks the header carries) lives in
 * ./ganttModel. This component decides where pixels go and nothing else.
 */

// Bars wider than this show their date range as inline text (FSD F: "showing
// the short date range as inline text once the bar is wide enough").
//
// The short date range is shown inline once the bar is wide enough. The FSD
// quotes 64px, a prototype measurement; at 64 the label clipped mid-word, so it
// was raised to 76 -- the measured width of "20 Mar – 06 Apr" at this font size.
//
// 76 was still too small, because it is the width of the TEXT and the bar also
// has to hold .barText's 8px of padding on each side. Anything between 77 and
// 97px therefore drew a label it could not fit, and the bar's overflow:hidden
// chopped it ("02 Feb – 02 Ma").
//
// Measured in Chrome at 0.6875rem/500 over the forms this renders: the widest,
// "01 Sep – 30 Sep", is 82.0px, so the bar needs 82 + 16 = 98. Below that the
// row shows no text at all, which is the FSD's own behaviour for a narrow bar;
// the full range is always in the tooltip either way.
const INLINE_TEXT_MIN_W = 98;

// The widest range this renders, measured in Chrome at .barText's size and
// weight: "01 Sep - 30 Sep" at 82.0px. Used to decide which SIDE of a narrow
// bar the label goes on, so it is a bound rather than a per-row measurement -
// measuring every row each frame would cost a layout pass per bar.
const DATE_LABEL_W = 82;

// Gap between a narrow bar and the label sitting beside it.
const OUTSIDE_TEXT_GAP = 6;

/**
 * Where a batch bar's date range goes.
 *
 * FSD F puts it inside the bar "once the bar is wide enough". Most batches are
 * a few weeks against a portfolio-wide axis, so most bars are NOT wide enough,
 * and the range simply vanished from them - dates appeared on a handful of rows
 * and not the rest, which reads as a glitch rather than as a rule.
 *
 * A bar that cannot hold its label now carries it immediately outside instead,
 * so every batch row states its dates. Right of the bar by default; left when
 * the right would run past the end of the lane.
 */
const dateLabelPlacement = (geo, laneWidth) => {
  if (!geo) return null;
  if (geo.width > INLINE_TEXT_MIN_W) return { inside: true };
  const right = geo.left + geo.width + OUTSIDE_TEXT_GAP;
  if (right + DATE_LABEL_W <= laneWidth) return { inside: false, left: right };
  const left = geo.left - OUTSIDE_TEXT_GAP - DATE_LABEL_W;
  // Neither side fits (a bar spanning the whole lane) - but such a bar is wide
  // enough to hold the label itself, so this only guards the degenerate case.
  if (left < 0) return { inside: true };
  return { inside: false, left };
};

// Half the Today pill's rendered width, so clamping its centre by this keeps
// the whole pill inside the lane.
const TODAY_PILL_PAD = 26;

const isSummary = (kind) => kind !== ROW_KIND.BATCH;

// Text colour that stays legible on the bar's own fill. The batch-level matrix
// colours are pale, so they take dark text; every deeper level takes white.
const barTextColourOf = (kind) => (kind === ROW_KIND.BATCH ? '#1B1B1B' : '#FFFFFF');

const GanttGrid = ({
  rows,
  zoom,
  expanded,
  onToggleExpand,
  onDrillCampaign,
  campaignRow,
  showProgress,
  today,
}) => {
  const classes = useStyles();

  // Available width drives the Portfolio+Quarter auto-fit (BR-07). Measured
  // from the scroll container minus the pinned label column.
  //
  // The same pass sizes the box vertically: it fills from its own top down to
  // the bottom of the window, so the chart (not the page) owns the vertical
  // scroll and the sticky timeline header stays in view however many rows are
  // expanded. It is a HEIGHT, not a maxHeight: FSD F and BR-13 both say the
  // chart panel fills the page area below the ribbon, and as a maxHeight a
  // short portfolio collapsed to its rows and left the rest of the page blank.
  const scrollRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [maxHeight, setMaxHeight] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      // FLOOR, and keep a pixel back. clientWidth can be fractional, and the
      // header bands and bars are absolutely positioned from a rounded left +
      // width — so a lane sized to the exact client width can be overrun by a
      // sub-pixel and raise a horizontal scrollbar on a chart that is supposed
      // to auto-fit (BR-07).
      const nextWidth = Math.max(0, Math.floor(el.clientWidth) - LABEL_COL_W - 1);
      setAvailableWidth((prev) => (prev === nextWidth ? prev : nextWidth));
      // Just the page's own bottom padding now — the 76px also reserved room
      // for the status legend, which FSD D retired.
      const top = el.getBoundingClientRect().top;
      const nextHeight = Math.max(320, Math.round(window.innerHeight - top - 24));
      setMaxHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };
    measure();
    // Re-measure on ANY size change of the box, not just a window resize:
    // expanding rows adds a vertical scrollbar, which narrows clientWidth by
    // its own width. Measured once at mount, the lane then no longer fitted and
    // a horizontal scrollbar appeared the first time somebody expanded a row.
    //
    // The callback is deferred to the next frame. Measuring synchronously inside
    // the observer sets state, which resizes the content, which notifies the
    // observer again - the browser reports that as "ResizeObserver loop", an
    // error event carrying NO error object, and webpack-dev-server's overlay
    // then dies on error.stack ("can't access property stack, error is
    // undefined"). A frame's delay breaks the cycle.
    let ro = null;
    let frame = 0;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          measure();
        });
      });
      if (scrollRef.current) ro.observe(scrollRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (frame) window.cancelAnimationFrame(frame);
      if (ro) ro.disconnect();
    };
  }, []);

  const visible = useMemo(() => flattenVisible(rows, expanded), [rows, expanded]);

  const window_ = useMemo(() => dateWindowOf(rows, { campaignRow }), [rows, campaignRow]);

  // Auto-fit applies at Portfolio view + Quarter zoom only; everything else
  // uses the fixed pixels-per-day scale and scrolls (BR-07).
  const autoFit = !campaignRow && zoom === ZOOM.QUARTER;
  const scale = useMemo(
    () => (window_ ? scaleOf(window_, zoom, availableWidth, { autoFit }) : null),
    [window_, zoom, availableWidth, autoFit]
  );

  const bands = useMemo(
    () => (window_ && scale ? headerBandsOf(window_, zoom, scale) : { major: [], minor: [] }),
    [window_, scale, zoom]
  );

  const todayX = useMemo(
    () => (window_ && scale ? todayXOf(window_, scale, today) : null),
    [window_, scale, today]
  );

  // Hover tooltip. Closed on mouse-leave and on any state change — zoom,
  // filter, expand/collapse (FSD C, E and J all require this).
  const [tip, setTip] = useState(null);
  React.useEffect(() => { setTip(null); }, [zoom, expanded, rows, campaignRow]);

  // Measured tooltip box, so the flip/clamp below works off its real size
  // rather than a guess (its height varies — the FSD's body rows are rendered
  // only where the underlying value exists). Seeded with a reasonable estimate
  // for the very first frame, then corrected once mounted.
  const tipRef = useRef(null);
  const [tipSize, setTipSize] = useState({ w: 220, h: 120 });
  React.useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = tipRef.current;
    setTipSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, [tip]);

  // The status filter chips were retired (FSD D), so "no rows" can no longer
  // mean "your filters excluded them" — it means this selection has nothing to
  // plot. The old wording quoted FSD D's retired filter message and would now
  // point the reader at a control that does not exist.
  if (!rows.length || !visible.length) {
    return <div className={classes.state}>No dated activity to plot for this selection.</div>;
  }
  if (!window_ || !scale) {
    return <div className={classes.state}>No dated activity to plot for this selection.</div>;
  }

  const laneWidth = scale.width;
  // FSD F and G quote these headings in caps: 'PROJECT / CAMPAIGN / STAGE /
  // BATCH' and 'STAGE / BATCH'. Applied via CSS so the strings stay readable.
  const colHeader = campaignRow ? 'Stage / Batch' : 'Project / Campaign / Stage / Batch';

  // Anchored to the bar's left edge. `geo.left` is measured inside the LANE,
  // but the tooltip is a child of the row body, whose origin is the left edge
  // of the pinned label column — so the label width has to be added back or the
  // tooltip lands a whole column to the left of the bar it describes.
  const openTip = (row, geo, rowIndex) => () => {
    // The chart scrolls on both axes, so the tooltip has to be placed against
    // what is ON SCREEN, not against the full content box — anchored to the
    // content alone it lands below the fold whenever the hovered row sits near
    // the bottom edge. Captured at hover time; if the user then scrolls, the
    // tooltip travels with its row, which is what you want anyway.
    const el = scrollRef.current;
    const view = el
      ? {
          top: el.scrollTop,
          left: el.scrollLeft,
          height: el.clientHeight,
          width: el.clientWidth,
        }
      : null;
    setTip({ row, laneLeft: geo.left, rowIndex, view });
  };

  // FSD K: "The status pill shows the system status label, while the bar colour
  // follows the mapped display status." FSD F says the same from the other side:
  // "Batch rows display their batch status in the pill (e.g. 'Closed', shown in
  // the Completed green), while Project, Campaign and Stage rows display their
  // rollup status."
  //
  // So the LABEL is the batch's own value where there is one, and the COLOUR is
  // always the mapped status. A batch reading 'Ongoing' shows a pill saying
  // Ongoing in In-Progress blue, not a pill saying In-Progress.
  //
  // Batch, Project and Campaign rows each carry a system status; a Stage does
  // not -- it has no status of its own -- so a Stage falls back to the mapped
  // label, which IS its rollup. That is FSD F exactly: "Batch, Project and
  // Campaign rows display their own system status in the pill ... while Stage
  // rows display their rollup status."
  const statusPill = (status, rawStatus, inferred) => {
    const c = STATUS_COLOURS[status] || STATUS_COLOURS[STATUS.PLANNED];
    const raw = String(rawStatus || '').trim();
    const label = pillLabel(raw, status);
    // Three things the pill may need to explain, in order of precedence: a
    // status read from dates because none was recorded (FSD K); a system word
    // this screen renames; a system word that maps to a different display
    // status. Whatever the pill shows, the system's own word is one hover away.
    let title;
    if (inferred) title = `${status} — from the row's dates; no status recorded`;
    else if (raw && raw !== label) title = `${raw} — shown as ${label}`;
    else if (label !== status) title = `${label} — shown as ${status}`;
    return (
      <span
        className={classes.rowStatusPill}
        style={{ background: `${c.fill}1A`, color: c.text }}
        title={title}
      >
        {label}
      </span>
    );
  };

  // FSD F: the row label runs caret -> coloured status dot -> name with its
  // sub-label beneath -> status pill right-aligned. The dot takes the status's
  // own fill, the same colour the pill tints and the bar is drawn in, so the
  // three always agree.
  const statusDot = (status) => {
    const c = STATUS_COLOURS[status] || STATUS_COLOURS[STATUS.PLANNED];
    return <span className={classes.rowStatusDot} style={{ background: c.fill }} />;
  };

  const renderTooltip = () => {
    if (!tip) return null;
    const r = tip.row;
    const c = STATUS_COLOURS[r.status] || STATUS_COLOURS[STATUS.PLANNED];
    const elapsed = elapsedFractionOf(r, today);
    // FSD J: body rows shown, in order, ONLY when the underlying value is
    // present — so each entry is conditional rather than rendered as a dash.
    const lines = [
      r.start && ['Start', longDate(r.start)],
      r.end && ['End', longDate(r.end)],
      r.start && r.end && ['Duration', `${inclusiveDays(r.start, r.end)} days`],
      elapsed != null && ['Elapsed', `${Math.round(elapsed * 100)}%`],
      r.plannedStart && ['Planned start', longDate(r.plannedStart)],
      r.plannedEnd && ['Planned end', longDate(r.plannedEnd)],
      // Product, 2026-10-01: the batch row's sub-label was dropped to keep rows
      // short, and its stage moved here "along with the target quantity and
      // actual quantity". Only on a batch row: every other row kind already
      // names its stage in its own title.
      r.kind === ROW_KIND.BATCH && r.stage && ['Stage', `Stage ${r.stage}`],
      r.yield && r.yield.target != null && ['Target yield', `${r.yield.target} ${r.yield.uom}`],
      r.yield && r.yield.actual != null && ['Actual yield', `${r.yield.actual} ${r.yield.uom}`],
    ].filter(Boolean);

    // Placement (FSD J: "flips above or below the row depending on the row's
    // vertical position"). The tooltip is a child of the row body, so its
    // coordinates are content coordinates; the visible band is that content
    // window minus the sticky header and the pinned label column, both of which
    // overlay it. Preferred position is just below the row, flipping above when
    // that would fall out of view, and clamped into the band as a last resort.
    // It is pointer-events: none, so overlaying a row costs nothing.
    const bodyH = visible.length * ROW_H;
    const view = tip.view;
    const visTop = view ? view.top : 0;
    const visBottom = view ? view.top + view.height - HEADER_H : bodyH;

    const rowTop = tip.rowIndex * ROW_H;
    const below = rowTop + ROW_H + 4;
    const above = rowTop - tipSize.h - 4;
    let top;
    if (below + tipSize.h <= visBottom) top = below;
    else if (above >= visTop) top = above;
    else top = Math.max(visTop, Math.min(below, visBottom - tipSize.h));

    // Horizontal: inside the visible timeline, clear of the pinned label column
    // (FSD J: "positioned to stay within the chart's content width").
    const minLeft = (view ? view.left : 0) + LABEL_COL_W + 8;
    const maxLeft = view
      ? view.left + view.width - tipSize.w - 8
      : LABEL_COL_W + laneWidth - tipSize.w - 8;
    const left = Math.max(minLeft, Math.min(LABEL_COL_W + tip.laneLeft, maxLeft));

    return (
      <div ref={tipRef} className={classes.tooltip} style={{ left, top }}>
        <div className={classes.tipHead}>
          <span className={classes.tipTitle}>{r.name}</span>
          {/* FSD J: the tooltip header "repeats the row's full title and its
              status pill", so it shows the same label the row does. */}
          <span className={classes.rowStatusPill} style={{ background: `${c.fill}1A`, color: c.text }}>
            {pillLabel(r.rawStatus, r.status)}
          </span>
        </div>
        {lines.map(([k, v]) => (
          <div className={classes.tipRow} key={k}>
            <span className={classes.tipKey}>{k}</span>
            <span className={classes.tipVal}>{v}</span>
          </div>
        ))}
        {r.kind === ROW_KIND.CAMPAIGN && (
          <div className={classes.tipDrill}>Click bar to drill into Campaign →</div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.chartBox}>
      <div
        className={classes.chartScroll}
        ref={scrollRef}
        style={maxHeight ? { height: maxHeight } : undefined}
      >
        <div className={classes.chartInner} style={{ width: LABEL_COL_W + laneWidth }}>
          {/* ── two-tier sticky timeline header (FSD H) ── */}
          <div className={classes.headerRow}>
            <div className={classes.labelHeader}>{colHeader}</div>
            <div className={classes.bandsWrap} style={{ width: laneWidth }}>
              {bands.major.map((t) => (
                <div key={`M${t.key}`} className={classes.bandMajor} style={{ left: t.left, width: t.width }}>
                  {t.label}
                </div>
              ))}
              {bands.minor.map((t) => (
                <div key={`m${t.key}`} className={classes.bandMinor} style={{ left: t.left, width: t.width }}>
                  {t.label}
                </div>
              ))}
              {/* Today pill anchored at the top of the header (FSD H). Its left
                  is clamped inside the lane: the pill is centred on its x, so at
                  either edge half of it would sit outside the lane — which both
                  cuts the word and widens the chart's scroll area. */}
              {todayX != null && (
                <div
                  className={classes.todayPill}
                  style={{ left: Math.min(Math.max(todayX, TODAY_PILL_PAD), laneWidth - TODAY_PILL_PAD) }}
                >
                  Today
                </div>
              )}
            </div>
          </div>

          {/* ── rows ── */}
          <div className={classes.body}>
            {visible.map((row, i) => {
              // Deeper levels get a lighter tint; the Project rule is skipped on
              // the very first row so it does not double up with the header's
              // own bottom border.
              const levelClass = {
                [ROW_KIND.PROJECT]: classes.rowProject,
                [ROW_KIND.CAMPAIGN]: classes.rowCampaign,
                [ROW_KIND.STAGE]: classes.rowStage,
              }[row.kind] || '';
              const geo = barGeometry(row, window_, scale);
              const colour = barColourOf(row.kind, row.status);
              const hasChildren = (row.children || []).length > 0;
              const open = expanded.has(row.id);
              const drillable = row.kind === ROW_KIND.CAMPAIGN;
              const elapsed = showProgress ? elapsedFractionOf(row, today) : null;
              // Batch rows only: FSD F gives the inline range to the full block
              // bar, and a summary row's span is already its children's.
              const datePlacement = !isSummary(row.kind) && row.start && row.end
                ? dateLabelPlacement(geo, laneWidth)
                : null;
              const dateLabel = datePlacement
                ? `${shortDate(row.start)} – ${shortDate(row.end)}`
                : null;

              return (
                <div
                  className={`${classes.row} ${levelClass}`}
                  key={row.id}
                  style={i === 0 ? { borderTop: 'none' } : undefined}
                >
                  {/* Pinned label column */}
                  <div className={classes.labelCell} style={{ paddingLeft: 16 + row.depth * 20 }}>
                    {hasChildren ? (
                      <button
                        type="button"
                        className={classes.twisty}
                        onClick={() => onToggleExpand(row.id)}
                        aria-label={open ? `Collapse ${row.name}` : `Expand ${row.name}`}
                      >
                        {open ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
                      </button>
                    ) : (
                      <span className={classes.twistySpacer} />
                    )}
                    {statusDot(row.status)}
                    <span className={classes.labelStack}>
                      <span className={classes.labelLine}>
                        <span
                          className={[
                            classes.rowName,
                            row.kind === ROW_KIND.PROJECT ? classes.rowNameProject : '',
                            row.kind === ROW_KIND.CAMPAIGN ? classes.rowNameCampaign : '',
                            drillable ? classes.rowNameDrillable : '',
                          ].filter(Boolean).join(' ')}
                          onClick={drillable ? () => onDrillCampaign(row) : undefined}
                          title={row.name}
                        >
                          {row.name}
                        </span>
                      </span>
                      {row.sublabel && (
                        <span className={classes.rowSub} title={row.sublabel}>{row.sublabel}</span>
                      )}
                    </span>
                    {/* Last child of the label cell, with the stack above taking
                        the slack, so the pill sits against the column's right
                        edge instead of hugging the name. */}
                    {statusPill(row.status, row.rawStatus, row.statusInferred)}
                  </div>

                  {/* Timeline lane */}
                  <div className={classes.lane} style={{ width: laneWidth }}>
                    {/* Grid lines behind the bars at every tick boundary (FSD H). */}
                    {bands.minor.map((t) => (
                      <div key={`gm${t.key}`} className={classes.gridLineMinor} style={{ left: t.left }} />
                    ))}
                    {bands.major.map((t) => (
                      <div key={`gM${t.key}`} className={classes.gridLineMajor} style={{ left: t.left }} />
                    ))}
                    {todayX != null && <div className={classes.todayLine} style={{ left: todayX }} />}

                    {geo && (isSummary(row.kind) ? (
                      /* Project / Campaign / Stage — slim bracket bar with end
                         caps, rather than a full block (FSD F Behavior). */
                      <div
                        className={classes.summaryBar}
                        style={{ left: geo.left, width: geo.width, cursor: drillable ? 'pointer' : 'default' }}
                        onMouseEnter={openTip(row, geo, i)}
                        onMouseLeave={() => setTip(null)}
                        onClick={drillable ? () => onDrillCampaign(row) : undefined}
                      >
                        <span className={classes.summarySpine} style={{ background: colour }} />
                        <span className={classes.summaryCap} style={{ left: 0, background: colour }} />
                        <span className={classes.summaryCap} style={{ right: 0, background: colour }} />
                      </div>
                    ) : (
                      /* Batch — full rounded block bar. */
                      <div
                        className={classes.bar}
                        style={{ left: geo.left, width: geo.width, background: colour }}
                        onMouseEnter={openTip(row, geo, i)}
                        onMouseLeave={() => setTip(null)}
                      >
                        {elapsed != null && (
                          <span className={classes.elapsed} style={{ width: `${elapsed * 100}%` }} />
                        )}
                        {datePlacement && datePlacement.inside && (
                          <span className={classes.barText} style={{ color: barTextColourOf(row.kind) }}>
                            {dateLabel}
                          </span>
                        )}
                      </div>
                    ))}

                    {/* A bar too narrow to hold its range states it alongside,
                        so no batch row is left without its dates. */}
                    {datePlacement && !datePlacement.inside && (
                      <span className={classes.barTextOutside} style={{ left: datePlacement.left }}>
                        {dateLabel}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {renderTooltip()}
          </div>
        </div>
      </div>
    </div>
  );
};

GanttGrid.propTypes = {
  // Row tree from buildRowTree (already status-filtered by the parent).
  rows: PropTypes.array.isRequired,
  zoom: PropTypes.string.isRequired,
  // Set of expanded row ids.
  expanded: PropTypes.object.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onDrillCampaign: PropTypes.func.isRequired,
  // The campaign being drilled into, or null at Portfolio view. Drives both the
  // date window and the label-column header.
  campaignRow: PropTypes.object,
  // FSD I / BR-09: the elapsed fill is suppressed entirely when off.
  showProgress: PropTypes.bool,
  today: PropTypes.instanceOf(Date),
};

GanttGrid.defaultProps = {
  campaignRow: null,
  showProgress: true,
  today: undefined,
};

export default GanttGrid;
